// Story 9.6 (ADO-1080), AC10 — the design-system Date Picker (Figma "Date Picker"
// 4919:1622, shown in situ at 4919:15617 and with a day chosen at 4919:15897).
//
// A NEW primitive. `apps/web/src/design-system/` had no calendar of any kind before
// this story: the epics' "consume the design-system Date Picker rather than defining a
// new calendar" note assumed one existed, and it did not (story D-2). It is built here
// rather than hand-rolled inside the Orders screen so the next date field in the app
// gets the same grid, the same keyboard model and the same a11y contract.
//
// ── The grid, and why it is a real `role="grid"` ──
// A calendar is a table of days and screen-reader users navigate it as one, so this is
// a WAI-ARIA grid (APG "Date Picker Dialog"): `role="grid"` over `role="row"` over
// `role="gridcell"`, with column headers naming the weekdays. Arrow keys move by day,
// PageUp/PageDown by month, Home/End to the ends of the week, and exactly ONE cell is
// in the tab order at a time (roving `tabIndex`), so Tab leaves the calendar rather
// than walking 35 buttons.
//
// The days are `<button>`s, not `<div role="button">`: Space/Enter and the focus ring come
// for free and correctly. Unavailable days are the exception below.
//
// ── Unavailable days (Story 8.13, ADO-2022) ──
// A day outside `min`/`max`, or one `isDayEnabled` refuses (8.13's delivery dates on offer), is
// shown but cannot be picked. It is `aria-disabled`, not natively `disabled`: a disabled button
// cannot take focus, so arrowing onto one would strand keyboard focus, which sparse enabled days
// (a few a month) would make the normal case. The APG date picker keeps such days focusable;
// the day's click handler refuses to select them, and Enter and Space reach that handler
// through the native button's own behaviour.
//
// ── What it does NOT own ──
// Opening, closing, positioning and returning focus to the trigger belong to the
// caller. `DateField` in this folder is that caller for the Orders filter panel; the
// split is the `Popover` contract this repo already uses (the panel is a panel, the
// trigger owns the toggle). What this component does own is Escape, because the key
// has to be handled where focus is.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../Icon/Icon';
import {
  WEEKDAY_LABELS,
  addDays,
  addMonthsToDay,
  addMonths,
  formatDayLabel,
  formatMonthHeading,
  isInsideRange,
  isWithinBounds,
  monthGrid,
  monthOf,
  weekdayIndex,
  type CalendarMonth,
  type IsoDay,
} from './calendar';
import styles from './DatePicker.module.css';

export interface DatePickerProps {
  /** The chosen day (`YYYY-MM-DD`), or null when the field is empty. */
  readonly value: string | null;
  /** Called with the chosen day. The caller decides whether that also closes the panel. */
  readonly onSelect: (day: IsoDay) => void;
  /** Escape, or a click outside if the caller wires one. */
  readonly onDismiss?: () => void;
  /** Earliest selectable day, inclusive. Days before it are present but disabled. */
  readonly min?: string;
  /** Latest selectable day, inclusive (AC9: the From picker's cap is the chosen To). */
  readonly max?: string;
  /** Story 8.13 (ADO-2022): which days inside the bounds may be picked (8.13: the delivery
   *  dates on offer). A day must pass this AND the bounds. Every day when absent. */
  readonly isDayEnabled?: (day: IsoDay) => boolean;
  /** The range being built, for the AC10 From/To highlight. Days strictly between the
   *  two get the wash; the ends are the selected day in their own picker. */
  readonly rangeStart?: string | null;
  readonly rangeEnd?: string | null;
  /** The viewer's today, which gets the ring. Passed in rather than read from the clock
   *  so a spec can pin it and the panel renders identically on every run. */
  readonly today: string;
  /** Names the calendar for assistive tech, e.g. "From date". */
  readonly 'aria-label': string;
  /** ADO-1779: `popup` is the floating panel under a field; `inline` draws the calendar
   *  flat inside a container that is already a surface (the mobile Filters sheet,
   *  Figma 5415:22466), so it drops the panel's own inset, corners and shadow, and the
   *  seven day columns share the width rather than holding 44px each. */
  readonly variant?: 'popup' | 'inline';
}

/**
 * Which day the calendar opens ON, and so which month it shows and where focus lands.
 *
 * The chosen day when there is one; otherwise today, pulled inside the bounds. That
 * last step matters: with a To date of 01/02/2026 already chosen, opening the From
 * picker on an unreachable "today" would show a month whose every day is disabled and
 * leave a keyboard user with nowhere to go.
 */
function initialFocusDay(value: string | null, today: string, min?: string, max?: string): IsoDay {
  if (value !== null && value !== '' && monthOf(value) !== null) return value;
  if (min !== undefined && min !== '' && today < min) return min;
  if (max !== undefined && max !== '' && today > max) return max;
  return today;
}

export function DatePicker({
  value,
  onSelect,
  onDismiss,
  min,
  max,
  isDayEnabled,
  rangeStart,
  rangeEnd,
  today,
  'aria-label': ariaLabel,
  variant = 'popup',
}: DatePickerProps) {
  const opensOn = initialFocusDay(value, today, min, max);
  const [focusedDay, setFocusedDay] = useState<IsoDay>(opensOn);
  const visibleMonth: CalendarMonth = useMemo(
    () => monthOf(focusedDay) ?? (monthOf(today) as CalendarMonth),
    [focusedDay, today],
  );
  const weeks = useMemo(() => monthGrid(visibleMonth), [visibleMonth]);

  /**
   * Move focus to the day cell only when the user MOVED it with the keyboard, never on
   * the initial render: the caller decides what gets focus when the panel opens (the
   * trigger keeps it until the user arrows into the grid), and stealing it here would
   * scroll a filter panel under someone who only clicked the calendar icon.
   */
  const gridRef = useRef<HTMLDivElement>(null);
  const keyboardMoveRef = useRef(false);
  useEffect(() => {
    if (!keyboardMoveRef.current) return;
    keyboardMoveRef.current = false;
    const cell = gridRef.current?.querySelector<HTMLButtonElement>('[data-focused="true"]');
    cell?.focus();
  }, [focusedDay]);

  const moveFocus = (next: IsoDay) => {
    keyboardMoveRef.current = true;
    setFocusedDay(next);
  };

  /** The month buttons move the grid AND the focused day together, so the roving cell
   *  is never left on a day the grid no longer shows. */
  const stepMonth = (offset: number) => {
    setFocusedDay(addMonthsToDay(focusedDay, offset));
  };

  const onGridKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const moves: Record<string, () => IsoDay> = {
      ArrowLeft: () => addDays(focusedDay, -1),
      ArrowRight: () => addDays(focusedDay, 1),
      ArrowUp: () => addDays(focusedDay, -7),
      ArrowDown: () => addDays(focusedDay, 7),
      // Home/End are the ends of the WEEK the focused day is in (APG), which is why
      // they use the Monday-first weekday index rather than the month's first/last day.
      Home: () => addDays(focusedDay, -weekdayIndex(focusedDay)),
      End: () => addDays(focusedDay, 6 - weekdayIndex(focusedDay)),
      PageUp: () => addMonthsToDay(focusedDay, -1),
      PageDown: () => addMonthsToDay(focusedDay, 1),
    };
    const move = moves[event.key];
    if (move !== undefined) {
      event.preventDefault();
      moveFocus(move());
      return;
    }
    if (event.key === 'Escape' && onDismiss !== undefined) {
      event.preventDefault();
      onDismiss();
    }
  };

  const previousMonth = addMonths(visibleMonth, -1);
  const nextMonth = addMonths(visibleMonth, 1);

  return (
    <div className={styles.panel} data-testid="date-picker" data-variant={variant}>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.nav}
          aria-label={`Previous month, ${formatMonthHeading(previousMonth)}`}
          onClick={() => stepMonth(-1)}
        >
          <Icon name="caretLeft" size={20} />
        </button>
        {/* An aria-live region, so arrowing across a month boundary announces the new
            month instead of silently changing the grid under the reader. */}
        <p className={styles.heading} aria-live="polite">
          {formatMonthHeading(visibleMonth)}
        </p>
        <button
          type="button"
          className={styles.nav}
          aria-label={`Next month, ${formatMonthHeading(nextMonth)}`}
          onClick={() => stepMonth(1)}
        >
          <Icon name="caretRight" size={20} />
        </button>
      </div>

      <div
        ref={gridRef}
        role="grid"
        aria-label={ariaLabel}
        className={styles.grid}
        // The key handler sits on the GRID and is reached by bubbling from whichever
        // day button holds focus, which is why one handler serves all 35 cells instead
        // of being attached to each. `tabIndex={-1}` makes the grid programmatically
        // focusable, which `jsx-a11y/interactive-supports-focus` requires of any element
        // carrying an interactive role and a key handler. It adds NO tab stop — the
        // roving `tabIndex={0}` on exactly one day cell is still the only one.
        tabIndex={-1}
        onKeyDown={onGridKeyDown}
      >
        <div role="row" className={styles.weekdays}>
          {WEEKDAY_LABELS.map((label) => (
            <span key={label} role="columnheader" className={styles.weekday}>
              {label}
            </span>
          ))}
        </div>
        {weeks.map((week) => (
          // The week's first real day is a stable key; a padded week cannot be empty
          // because `monthGrid` never emits an all-null row.
          <div role="row" className={styles.week} key={week.find((day) => day !== null) ?? ''}>
            {week.map((day, columnIndex) =>
              day === null ? (
                // A blank still occupies a grid cell, so the row stays seven wide for
                // assistive tech and the columns stay aligned visually.
                <span
                  role="gridcell"
                  aria-hidden
                  className={styles.blank}
                  key={`blank-${String(columnIndex)}`}
                />
              ) : (
                <DayCell
                  key={day}
                  day={day}
                  selected={value === day}
                  isToday={day === today}
                  focused={day === focusedDay}
                  inRange={isInsideRange(day, rangeStart, rangeEnd)}
                  unavailable={
                    !isWithinBounds(day, min, max) ||
                    (isDayEnabled !== undefined && !isDayEnabled(day))
                  }
                  onSelect={onSelect}
                />
              ),
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function DayCell({
  day,
  selected,
  isToday,
  focused,
  inRange,
  unavailable,
  onSelect,
}: {
  day: IsoDay;
  selected: boolean;
  isToday: boolean;
  focused: boolean;
  inRange: boolean;
  unavailable: boolean;
  onSelect: (day: IsoDay) => void;
}) {
  return (
    // `aria-selected` belongs on the GRIDCELL, not on the button inside it: it is not a
    // supported attribute of `role="button"` and axe flags it as a critical violation
    // there. The cell is what a grid announces as chosen, and the button is the widget
    // that changes the choice. `aria-current="date"` is the separate "this is today"
    // fact, valid on any element and deliberately independent of the selection (today
    // is usually not the selected day).
    <span role="gridcell" aria-selected={selected} className={styles.cell}>
      <button
        type="button"
        className={styles.day}
        // The roving tab stop: exactly one cell is reachable with Tab, and arrow keys
        // move it. 35 tab stops would make the calendar unusable by keyboard.
        tabIndex={focused ? 0 : -1}
        data-focused={focused}
        data-selected={selected}
        data-today={isToday}
        data-in-range={inRange}
        aria-current={isToday ? 'date' : undefined}
        aria-label={formatDayLabel(day)}
        // Focusable but not selectable (see the file header): the click handler refuses it;
        // Enter and Space reach that handler through native button behaviour.
        aria-disabled={unavailable || undefined}
        onClick={() => {
          if (!unavailable) onSelect(day);
        }}
      >
        {Number(day.slice(8, 10))}
      </button>
    </span>
  );
}
